import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Heart, MessageCircle, Send, MoreHorizontal, Trash2, Flag, VolumeX, X, CornerDownRight } from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import ReportModal from '../components/ReportModal';
import CommentRow from '../components/CommentRow';
import { formatDistanceToNow } from 'date-fns';

function getInitials(name) {
  return (name || '?').split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
}

export default function VibeDetail() {
  const { id: vibeId } = useParams();
  const [searchParams] = useSearchParams();
  const highlightId = searchParams.get('highlight') || '';
  const autoReply = searchParams.get('reply') === '1';
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [vibe, setVibe] = useState(null);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newComment, setNewComment] = useState('');
  const [posting, setPosting] = useState(false);
  const [liked, setLiked] = useState(false);
  const [likesCount, setLikesCount] = useState(0);
  const [menuFor, setMenuFor] = useState(null); // comment object
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [reportUser, setReportUser] = useState(null);
  const [vibeMenuOpen, setVibeMenuOpen] = useState(false);
  const [flashId, setFlashId] = useState('');
  const [composerPulse, setComposerPulse] = useState(false);
  // Threading state
  const [replyTo, setReplyTo] = useState(null);              // { commentId, username, displayName }
  const [expandedThreads, setExpandedThreads] = useState({}); // { [commentId]: fullReplies[] }
  const commentRefs = useRef({});
  const composerRef = useRef(null);
  const scrolledRef = useRef(false);
  const autoReplyHandledRef = useRef(false);
  const startReplyRef = useRef(null);

  const isVibeOwner = vibe && currentUser && vibe.user?.id === currentUser.id;

  const fetchAll = useCallback(async () => {
    try {
      const [v, c] = await Promise.all([
        api.get(`/vibes/${vibeId}`),
        api.get(`/vibes/${vibeId}/comments`),
      ]);
      setVibe(v.data);
      setLiked(!!v.data.is_liked);
      setLikesCount(v.data.likes_count || 0);
      setComments(c.data);
    } catch {
      navigate(-1);
    }
    setLoading(false);
  }, [vibeId, navigate]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Scroll to highlighted comment once loaded. If the target comment is a
  // REPLY (not a top-level comment), resolve its parent via /comments/:id
  // and auto-expand THAT specific parent's thread before scrolling. We can't
  // guess the parent from the inline replies cache — multiple top-level
  // comments may have unloaded replies, and we'd pick the wrong one.
  useEffect(() => {
    if (!highlightId || scrolledRef.current || comments.length === 0) return;

    const isTopLevel = comments.some(c => c.id === highlightId);
    const alreadyInline = !isTopLevel && comments.some(c =>
      (c.replies || []).some(r => r.id === highlightId));

    if (!isTopLevel && !alreadyInline) {
      // Not yet in the DOM. Look it up to find the true parent_id.
      (async () => {
        try {
          const { data: cmt } = await api.get(`/vibes/comments/${highlightId}`);
          const parentId = cmt?.parent_id;
          if (!parentId) return; // shouldn't happen, but no-op safely
          if (!expandedThreads[parentId]) {
            const { data: replies } = await api.get(`/vibes/comments/${parentId}/replies`);
            setExpandedThreads(prev => ({ ...prev, [parentId]: replies }));
          }
        } catch { /* no-op */ }
      })();
      return; // wait for replies to render, then this effect re-runs
    }

    const el = commentRefs.current[highlightId];
    if (el) {
      scrolledRef.current = true;
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setFlashId(highlightId);
      setTimeout(() => setFlashId(''), 2400);

      // If we arrived via the notification "Reply" CTA, auto-trigger reply
      // mode for this comment so the composer is already focused and
      // pre-filled with @mention. One-shot — `autoReplyHandled` guards a
      // re-fire if the parent re-renders.
      if (autoReply && !autoReplyHandledRef.current) {
        autoReplyHandledRef.current = true;
        // Find the comment record + its parent if any
        let target = comments.find(c => c.id === highlightId);
        let parentId = null;
        if (!target) {
          for (const c of comments) {
            const r = (expandedThreads[c.id] || c.replies || []).find(r => r.id === highlightId);
            if (r) { target = r; parentId = c.id; break; }
          }
        }
        if (target) {
          // Tiny delay so scroll settles before keyboard opens
          setTimeout(() => startReplyRef.current?.(target, parentId), 450);
        }
      }
    }
  }, [highlightId, comments, expandedThreads, autoReply]);

  const handleLike = async () => {
    try {
      if (liked) {
        await api.delete(`/vibes/${vibeId}/like`);
        setLiked(false);
        setLikesCount(c => c - 1);
      } else {
        await api.post(`/vibes/${vibeId}/like`);
        setLiked(true);
        setLikesCount(c => c + 1);
      }
    } catch {}
  };

  const handlePost = async (e) => {
    e.preventDefault();
    const text = newComment.trim();
    if (!text || posting) return;
    setPosting(true);
    try {
      const payload = { text };
      if (replyTo?.commentId) payload.parent_id = replyTo.commentId;
      await api.post(`/vibes/${vibeId}/comments`, payload);
      setNewComment('');
      setReplyTo(null);
      // Refetch the threaded tree so replies bucket under the correct parent
      // consistently. If the GET fails for any reason, keep the existing
      // optimistic list rather than blanking the UI out.
      try {
        const { data: fresh } = await api.get(`/vibes/${vibeId}/comments`);
        if (Array.isArray(fresh)) setComments(fresh);
      } catch { /* keep existing list */ }
    } catch {}
    setPosting(false);
  };

  const handleCommentLike = async (comment, parentId) => {
    const isLiked = !!comment.is_liked;
    // Optimistic update
    const apply = (updater) => {
      setComments(prev => prev.map(c => {
        if (c.id === comment.id) return updater(c);
        if (parentId && c.id === parentId) {
          return { ...c, replies: (c.replies || []).map(r => r.id === comment.id ? updater(r) : r) };
        }
        return c;
      }));
      setExpandedThreads(prev => {
        if (!parentId || !prev[parentId]) return prev;
        return { ...prev, [parentId]: prev[parentId].map(r => r.id === comment.id ? updater(r) : r) };
      });
    };
    apply(c => ({ ...c, is_liked: !isLiked, likes_count: Math.max(0, (c.likes_count || 0) + (isLiked ? -1 : 1)) }));
    try {
      if (isLiked) await api.delete(`/vibes/comments/${comment.id}/like`);
      else await api.post(`/vibes/comments/${comment.id}/like`);
    } catch {
      // Rollback on failure
      apply(c => ({ ...c, is_liked: isLiked, likes_count: Math.max(0, (c.likes_count || 0) + (isLiked ? 1 : -1)) }));
    }
  };

  const startReply = (comment, parentId) => {
    // If we're replying to a reply, the actual API parent is the top-level comment
    const targetParentId = parentId || comment.id;
    setReplyTo({
      commentId: targetParentId,
      username: comment.user?.username || '',
      displayName: comment.user?.display_name || comment.user?.username || 'comment',
    });
    // Pre-fill the composer with @mention so it's clear who's being replied to
    setNewComment(prev => {
      const handle = comment.user?.username ? `@${comment.user.username} ` : '';
      // Only inject if the box doesn't already start with this handle
      return prev.startsWith(handle) ? prev : handle;
    });
    // Scroll composer into view (in case the page is scrolled mid-thread)
    // AND focus the input — both are needed to give the user a clear cue
    // that their Reply tap landed. The composer is `fixed bottom-0` so it's
    // always in viewport, but on tall pages scrollIntoView still pulls the
    // tapped comment closer to the composer for context. Pulse animation
    // confirms the state change visually.
    setTimeout(() => {
      try { composerRef.current?.focus({ preventScroll: false }); } catch {}
      composerRef.current?.scrollIntoView({ block: 'end', behavior: 'smooth' });
    }, 50);
    setComposerPulse(true);
    setTimeout(() => setComposerPulse(false), 700);
  };
  // Keep a ref so the deep-link effect can call startReply without
  // re-running every time the function identity changes.
  startReplyRef.current = startReply;

  const expandReplies = async (commentId) => {
    if (expandedThreads[commentId]) {
      // Collapse
      setExpandedThreads(prev => { const x = { ...prev }; delete x[commentId]; return x; });
      return;
    }
    try {
      const { data } = await api.get(`/vibes/comments/${commentId}/replies`);
      setExpandedThreads(prev => ({ ...prev, [commentId]: data }));
    } catch {}
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/vibes/comments/${confirmDelete.id}`);
      const targetId = confirmDelete.id;
      const targetParentId = confirmDelete.parent_id || null;
      if (targetParentId) {
        // It's a reply — remove from parent's inline replies AND from expanded thread cache.
        setComments(prev => prev.map(c => {
          if (c.id !== targetParentId) return c;
          const filtered = (c.replies || []).filter(r => r.id !== targetId);
          return { ...c, replies: filtered, replies_count: Math.max(0, (c.replies_count || 0) - 1) };
        }));
        setExpandedThreads(prev => {
          if (!prev[targetParentId]) return prev;
          return { ...prev, [targetParentId]: prev[targetParentId].filter(r => r.id !== targetId) };
        });
      } else {
        // Top-level — drop the whole branch.
        setComments(prev => prev.filter(c => c.id !== targetId));
      }
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to delete');
    }
    setConfirmDelete(null);
    setMenuFor(null);
  };

  const handleMute = async (userId) => {
    try {
      await api.post(`/users/${userId}/mute`);
    } catch {}
    setMenuFor(null);
  };

  if (loading || !vibe) {
    return (
      <div className="content-area px-4 pt-4 space-y-3">
        <div className="skeleton h-8 w-24" />
        <div className="skeleton h-32 rounded-2xl" />
      </div>
    );
  }

  return (
    <div className="content-area animate-fade-in" data-testid="vibe-detail-page">
      {/* Header */}
      <div className="px-4 pt-5 pb-3 flex items-center gap-3 sticky top-0 bg-[#F7F6F2] z-10">
        <button onClick={() => navigate(-1)} data-testid="vibe-back">
          <ArrowLeft size={22} className="text-[#111111]" />
        </button>
        <h1 className="text-lg font-bold text-[#111111]">Vibe</h1>
        {vibe && !isVibeOwner && (
          <button
            data-testid="vibe-detail-menu"
            onClick={() => setVibeMenuOpen(true)}
            className="ml-auto p-2 text-[#9B9EA3] hover:text-[#111111] transition-colors"
            aria-label="Vibe options"
          >
            <MoreHorizontal size={20} />
          </button>
        )}
      </div>

      {/* Vibe */}
      <div className="px-4">
        <div className="bg-white rounded-2xl border border-black/[0.06] p-4">
          <div className="flex items-start gap-3">
            <button
              onClick={() => vibe.user?.id && navigate(`/user/${vibe.user.id}`)}
              className="flex-shrink-0"
            >
              {vibe.user?.profile_photo ? (
                <img loading="lazy" decoding="async" src={vibe.user.profile_photo} alt="" className="w-10 h-10 rounded-full object-cover" />
              ) : (
                <div className="w-10 h-10 rounded-full bg-[#F1F0EA] flex items-center justify-center text-xs font-bold text-[#9B9EA3]">
                  {getInitials(vibe.user?.display_name)}
                </div>
              )}
            </button>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-semibold text-sm text-[#111111]">{vibe.user?.display_name}</span>
                <span className="text-[11px] text-[#9B9EA3]">@{vibe.user?.username}</span>
              </div>
              <p className="text-sm text-[#2A2A2A] mt-1.5 leading-relaxed whitespace-pre-wrap">{vibe.text}</p>
              {vibe.image && (
                <img loading="lazy" decoding="async" src={vibe.image} alt="" className="mt-3 rounded-xl w-full max-h-[320px] object-cover" />
              )}
              <div className="flex items-center gap-5 mt-3">
                <button
                  data-testid="vibe-detail-like"
                  onClick={handleLike}
                  className="flex items-center gap-1.5 text-xs transition-transform active:scale-95"
                >
                  <Heart size={18} className={liked ? 'fill-[#FF3B30] text-[#FF3B30]' : 'text-[#9B9EA3]'} />
                  <span className={liked ? 'text-[#FF3B30]' : 'text-[#9B9EA3]'}>{likesCount}</span>
                </button>
                <div className="flex items-center gap-1.5 text-xs text-[#9B9EA3]">
                  <MessageCircle size={18} />
                  <span>{comments.length}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Comments */}
      <div className="px-4 pt-4 pb-[160px]" data-testid="comments-list">
        <h2 className="text-xs font-bold text-[#9B9EA3] uppercase tracking-wider mb-3">Comments</h2>
        {comments.length === 0 ? (
          <p className="text-sm text-[#9B9EA3] text-center py-8">Be the first to comment.</p>
        ) : (
          <div className="space-y-3">
            {comments.map(c => {
              const isCommentAuthor = currentUser?.id === c.user?.id;
              const canDelete = isCommentAuthor || isVibeOwner;
              const expandedReplies = expandedThreads[c.id];
              const visibleReplies = expandedReplies || c.replies || [];
              const hasMoreReplies = (c.replies_count || 0) > visibleReplies.length;
              return (
                <CommentRow
                  key={c.id}
                  comment={c}
                  parentId={null}
                  canDelete={canDelete}
                  currentUser={currentUser}
                  flashId={flashId}
                  commentRefs={commentRefs}
                  navigate={navigate}
                  onLike={handleCommentLike}
                  onReply={startReply}
                  onMenu={(comment) => setMenuFor({ ...comment, _canDelete: canDelete, parent_id: null })}
                  replies={visibleReplies}
                  hasMoreReplies={hasMoreReplies}
                  onToggleReplies={expandReplies}
                  isExpanded={!!expandedReplies}
                  expandedTotal={(c.replies_count || 0) - visibleReplies.length}
                  setMenuFor={setMenuFor}
                />
              );
            })}
          </div>
        )}
      </div>

      {/* Composer — pinned at the bottom. z-[10000] keeps it ABOVE any
          fixed siblings (defence-in-depth; BottomNav is also hidden on
          /vibe/ routes via App.js). Reply CTA on a comment focuses this
          input and pre-fills @username. */}
      <div className={`fixed bottom-0 left-0 right-0 max-w-[480px] mx-auto bg-white border-t border-black/[0.06] px-3 py-2.5 pb-[max(10px,env(safe-area-inset-bottom))] z-[10000] transition-shadow ${composerPulse ? 'shadow-[0_-8px_24px_rgba(182,230,0,0.45)] ring-2 ring-[#B6E600]' : ''}`} data-testid="vibe-comment-composer">
        {replyTo && (
          <div className="flex items-center gap-2 mb-2 px-1" data-testid="reply-to-chip">
            <CornerDownRight size={12} className="text-[#9B9EA3]" />
            <span className="text-[11px] text-[#6B6E73]">
              Replying to <span className="font-bold text-[#111]">@{replyTo.username || replyTo.displayName}</span>
            </span>
            <button
              onClick={() => { setReplyTo(null); setNewComment(''); }}
              data-testid="cancel-reply-btn"
              className="ml-auto text-[#9B9EA3] active:scale-90"
              aria-label="Cancel reply"
            >
              <X size={14} />
            </button>
          </div>
        )}
        <form onSubmit={handlePost} className="flex items-end gap-2">
          <input
            ref={composerRef}
            data-testid="vibe-comment-input"
            type="text"
            value={newComment}
            onChange={e => setNewComment(e.target.value)}
            placeholder={replyTo ? `Reply to @${replyTo.username || replyTo.displayName}…` : 'Add a comment...'}
            className="flex-1 bg-[#F1F0EA] text-[#111111] rounded-full px-4 py-2.5 text-sm focus:outline-none placeholder-[#B0B3B8]"
          />
          <button
            type="submit"
            data-testid="vibe-comment-submit"
            disabled={!newComment.trim() || posting}
            className="w-10 h-10 rounded-full bg-[#B6E600] text-[#111111] flex items-center justify-center active:scale-90 disabled:opacity-40 transition-transform"
          >
            <Send size={18} strokeWidth={2.5} />
          </button>
        </form>
      </div>

      {/* Comment Actions Sheet */}
      {menuFor && (
        <div
          className="fixed inset-0 z-[10000] flex items-end justify-center bg-black/30 animate-fade-in"
          onClick={() => setMenuFor(null)}
          data-testid="comment-menu-overlay"
        >
          <div
            className="w-full max-w-[480px] bg-white rounded-t-3xl pb-safe-area animate-slide-up"
            onClick={e => e.stopPropagation()}
            data-testid="comment-menu-sheet"
          >
            <div className="flex justify-center pt-3 pb-1">
              <div className="w-10 h-1 bg-[#E5E4DE] rounded-full" />
            </div>
            <div className="px-5 pt-3 pb-1">
              <p className="text-sm font-bold text-[#111111]">@{menuFor.user?.username}</p>
            </div>
            <div className="px-3 pt-2">
              {menuFor._canDelete && (
                <button
                  data-testid="comment-delete-btn"
                  onClick={() => { setConfirmDelete(menuFor); }}
                  className="w-full flex items-center gap-4 p-3 rounded-2xl active:bg-[#F1F0EA] transition-colors text-left"
                >
                  <div className="w-10 h-10 rounded-2xl bg-[#FF3B30]/10 flex items-center justify-center flex-shrink-0">
                    <Trash2 size={18} className="text-[#FF3B30]" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-[#FF3B30]">Delete Comment</p>
                    <p className="text-xs text-[#9B9EA3] mt-0.5">Remove this comment permanently</p>
                  </div>
                </button>
              )}
              {currentUser?.id !== menuFor.user?.id && (
                <>
                  <button
                    data-testid="comment-mute-btn"
                    onClick={() => menuFor.user?.id && handleMute(menuFor.user.id)}
                    className="w-full flex items-center gap-4 p-3 rounded-2xl active:bg-[#F1F0EA] transition-colors text-left"
                  >
                    <div className="w-10 h-10 rounded-2xl bg-[#F1F0EA] flex items-center justify-center flex-shrink-0">
                      <VolumeX size={18} className="text-[#111111]" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-[#111111]">Mute User</p>
                      <p className="text-xs text-[#9B9EA3] mt-0.5">Hide their vibes and plans from your feed</p>
                    </div>
                  </button>
                  <button
                    data-testid="comment-report-btn"
                    onClick={() => { setMenuFor(null); setReportUser(menuFor.user); }}
                    className="w-full flex items-center gap-4 p-3 rounded-2xl active:bg-[#F1F0EA] transition-colors text-left"
                  >
                    <div className="w-10 h-10 rounded-2xl bg-[#FF3B30]/10 flex items-center justify-center flex-shrink-0">
                      <Flag size={18} className="text-[#FF3B30]" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-[#FF3B30]">Report User</p>
                      <p className="text-xs text-[#9B9EA3] mt-0.5">Flag behavior that breaks our guidelines</p>
                    </div>
                  </button>
                </>
              )}
            </div>
            <div className="px-5 pt-4">
              <button
                onClick={() => setMenuFor(null)}
                className="w-full py-3 rounded-full bg-[#F1F0EA] text-sm font-medium text-[#111111]"
                data-testid="comment-menu-cancel"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Delete Modal */}
      {confirmDelete && (
        <div
          className="fixed inset-0 z-[10010] flex items-center justify-center bg-black/40 px-6 animate-fade-in"
          onClick={() => setConfirmDelete(null)}
          data-testid="comment-delete-confirm-overlay"
        >
          <div
            className="w-full max-w-[360px] bg-white rounded-3xl p-5 animate-slide-up"
            onClick={e => e.stopPropagation()}
            data-testid="comment-delete-confirm"
          >
            <div className="flex items-start justify-between mb-2">
              <h3 className="text-base font-bold text-[#111111]">Delete comment?</h3>
              <button onClick={() => setConfirmDelete(null)} className="text-[#9B9EA3] -mt-1 -mr-1">
                <X size={18} />
              </button>
            </div>
            <p className="text-sm text-[#6B6E73] leading-relaxed">
              This action cannot be undone.
            </p>
            <div className="mt-5 grid grid-cols-2 gap-2">
              <button
                onClick={() => setConfirmDelete(null)}
                className="py-3 rounded-full bg-[#F1F0EA] text-sm font-semibold text-[#111111] active:scale-[0.98] transition-transform"
                data-testid="comment-delete-cancel"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="py-3 rounded-full bg-[#FF3B30] text-sm font-bold text-white active:scale-[0.98] transition-transform"
                data-testid="comment-delete-confirm-btn"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Report User Modal */}
      {reportUser && (
        <ReportModal type="user" targetId={reportUser.id} onClose={() => setReportUser(null)} />
      )}

      {/* Vibe-level Actions Sheet */}
      {vibeMenuOpen && vibe?.user && (
        <div
          className="fixed inset-0 z-[10000] flex items-end justify-center bg-black/30 animate-fade-in"
          onClick={() => setVibeMenuOpen(false)}
          data-testid="vibe-detail-menu-sheet"
        >
          <div
            className="w-full max-w-[480px] bg-white rounded-t-3xl pb-safe-area animate-slide-up"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex justify-center pt-3 pb-1">
              <div className="w-10 h-1 bg-[#E5E4DE] rounded-full" />
            </div>
            <div className="px-5 pt-3 pb-1">
              <p className="text-sm font-bold text-[#111111]">@{vibe.user.username}</p>
            </div>
            <div className="px-3 pt-2">
              <button
                data-testid="vibe-detail-mute-author"
                onClick={() => { setVibeMenuOpen(false); handleMute(vibe.user.id); }}
                className="w-full flex items-center gap-4 p-3 rounded-2xl active:bg-[#F1F0EA] transition-colors text-left"
              >
                <div className="w-10 h-10 rounded-2xl bg-[#F1F0EA] flex items-center justify-center flex-shrink-0">
                  <VolumeX size={18} className="text-[#111111]" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-[#111111]">Mute User</p>
                  <p className="text-xs text-[#9B9EA3] mt-0.5">Hide their vibes and plans from your feed</p>
                </div>
              </button>
              <button
                data-testid="vibe-detail-report-author"
                onClick={() => { setVibeMenuOpen(false); setReportUser(vibe.user); }}
                className="w-full flex items-center gap-4 p-3 rounded-2xl active:bg-[#F1F0EA] transition-colors text-left"
              >
                <div className="w-10 h-10 rounded-2xl bg-[#FF3B30]/10 flex items-center justify-center flex-shrink-0">
                  <Flag size={18} className="text-[#FF3B30]" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-[#FF3B30]">Report Vibe</p>
                  <p className="text-xs text-[#9B9EA3] mt-0.5">Flag content that breaks our guidelines</p>
                </div>
              </button>
            </div>
            <div className="px-5 pt-4">
              <button
                onClick={() => setVibeMenuOpen(false)}
                className="w-full py-3 rounded-full bg-[#F1F0EA] text-sm font-medium text-[#111111]"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
